import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiPost } from "@/lib/apiClient";
import { ArrowLeft } from "lucide-react";

type FinanceItem = { id: string; name?: string };

export function FinanceItemNewPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sku, setSku] = useState("");
  const [unitPrice, setUnitPrice] = useState("0");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    const res = await apiPost<FinanceItem>("/api/finance/items", {
      name: name.trim(),
      description: description.trim() || null,
      sku: sku.trim() || null,
      unitPrice: Number(unitPrice) || 0,
      active: true,
    });
    setSaving(false);
    if (!res.ok || !res.data?.id) {
      setError(res.error || "Could not create item.");
      return;
    }
    void navigate(`/finance/items/${encodeURIComponent(res.data.id)}`);
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to="/finance/items"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All items
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">New catalog item</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">POST /api/finance/items</p>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      <form onSubmit={(e) => void onSubmit(e)} className="max-w-xl space-y-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-950/40">
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Name *</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">SKU</span>
          <input
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Unit price</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create item"}
        </button>
      </form>
    </div>
  );
}
